// <viewport-video> — a brand video that plays only while it is on screen,
// with a centred play / pause button layered over the browser's native
// controls.
//
// Markup contract:
//   <viewport-video data-state="paused" data-play-threshold="0.35">
//     <video preload="none" muted playsinline controls ...>   (one or more —
//     a hidden desktop/mobile variant is display:none and is ignored)
//     <button data-video-toggle data-label-play=".." data-label-pause="..">
//   </viewport-video>
//
// Behaviour:
//   - Videos ship with preload="none" and no autoplay, so nothing downloads
//     at page load. We warm the active video one viewport ahead and start
//     it (muted) once >= threshold of the element is visible; scrolling
//     away, or hiding the tab, pauses it.
//   - The centred button AND the native controls both toggle playback. Any
//     pause we did not trigger ourselves (button, native control bar,
//     keyboard) is treated as the visitor's choice: the video stays paused
//     until they press play again, even if it scrolls out and back in.
//   - If the visitor unmutes via the native controls we keep the sound on
//     when resuming; we only fall back to muted if the browser refuses to
//     autoplay with audio.
//   - prefers-reduced-motion: reduce -> no autoplay; the controls are the
//     only way to start the video.
//   - play() is gated on canplay: calling play() on a cold video makes
//     Safari drop the poster before the first frame is decoded.
//   - data-state ("playing" | "paused") drives the icon swap in CSS and the
//     button's aria-label follows it.
if (!customElements.get('viewport-video')) {
  customElements.define(
    'viewport-video',
    class ViewportVideo extends HTMLElement {
      connectedCallback() {
        if (this.dataset.viewportVideoInit) return
        this.dataset.viewportVideoInit = '1'

        this.videos = Array.from(this.querySelectorAll('video'))
        this.button = this.querySelector('[data-video-toggle]')
        if (!this.videos.length) return

        this.inView = false
        this.userPaused = false
        // Number of pause events we are expecting from our own pause()
        // calls; any pause event beyond that came from the visitor.
        this.expectedPauses = 0
        this.reduceMotion =
          window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches

        this.videos.forEach((video) => {
          video.muted = true
          video.removeAttribute('autoplay')
          video.addEventListener('play', () => {
            // Started by us or by the visitor — either way it is wanted.
            this.userPaused = false
            this.syncState()
          })
          video.addEventListener('pause', () => {
            if (this.expectedPauses > 0) {
              this.expectedPauses--
            } else if (!video.ended && !document.hidden) {
              // Native control bar, keyboard, or the centred button.
              this.userPaused = true
            }
            this.syncState()
          })
          video.addEventListener('ended', () => this.syncState())
        })

        if (this.button) {
          this.button.addEventListener('click', (e) => {
            e.preventDefault()
            this.toggle()
          })
        }

        this.onVisibility = () => {
          if (document.hidden) {
            this.pauseAll()
          } else if (this.inView && !this.userPaused && !this.reduceMotion) {
            this.playActive()
          }
        }
        document.addEventListener('visibilitychange', this.onVisibility)

        // Breakpoint change swaps which <video> is visible.
        this.onResize = () => this.syncActive()
        window.addEventListener('resize', this.onResize)

        this.observe()
        this.syncState()
      }

      disconnectedCallback() {
        document.removeEventListener('visibilitychange', this.onVisibility)
        window.removeEventListener('resize', this.onResize)
        if (this.warmObserver) this.warmObserver.disconnect()
        if (this.playObserver) this.playObserver.disconnect()
        this.pauseAll()
      }

      // The display:none variant has no offsetParent.
      get activeVideo() {
        return (
          this.videos.find((v) => v.offsetParent !== null) || this.videos[0]
        )
      }

      warm(video) {
        if (!video || video.dataset.warmed) return
        video.dataset.warmed = '1'
        video.preload = 'auto'
        if (video.readyState === 0) {
          try {
            video.load()
          } catch (e) {}
        }
      }

      // Play, keeping whatever mute state the visitor chose; if the browser
      // blocks unmuted autoplay, fall back to muted so the video still runs.
      tryPlay(video) {
        const attempt = video.play()
        if (!attempt || !attempt.catch) return
        attempt.catch(() => {
          if (!video.muted) {
            video.muted = true
            video.play().catch(() => {})
          }
        })
      }

      playWhenReady(video) {
        if (!video) return
        if (video.ended && !video.loop) {
          video.currentTime = 0
        }
        if (video.readyState >= 3) {
          this.tryPlay(video)
          return
        }
        this.warm(video)
        if (video.dataset.pendingPlay) return
        video.dataset.pendingPlay = '1'
        const onReady = () => {
          video.removeEventListener('canplay', onReady)
          delete video.dataset.pendingPlay
          // Only start if we still want it playing (not scrolled away /
          // paused by the visitor while buffering).
          if (this.wantsPlay()) this.tryPlay(video)
        }
        video.addEventListener('canplay', onReady)
      }

      wantsPlay() {
        return this.inView && !this.userPaused && !document.hidden
      }

      pauseVideo(video) {
        if (!video || video.paused) return
        this.expectedPauses++
        video.pause()
      }

      playActive() {
        const active = this.activeVideo
        this.videos.forEach((v) => {
          if (v !== active) this.pauseVideo(v)
        })
        this.playWhenReady(active)
      }

      pauseAll() {
        this.videos.forEach((v) => this.pauseVideo(v))
      }

      syncActive() {
        if (!this.wantsPlay() || this.reduceMotion) return
        const active = this.activeVideo
        if (active.paused) this.playActive()
      }

      toggle() {
        const active = this.activeVideo
        if (active.paused || active.ended) {
          this.userPaused = false
          this.playActive()
        } else {
          // Not counted as expected: this IS the visitor pausing.
          active.pause()
        }
      }

      observe() {
        if (!('IntersectionObserver' in window)) {
          // Very old browsers: leave it paused, the controls still work.
          return
        }
        this.warmObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return
              this.warm(this.activeVideo)
              this.warmObserver.disconnect()
            })
          },
          { rootMargin: '100% 0px', threshold: 0 }
        )
        this.warmObserver.observe(this)

        const threshold = parseFloat(this.dataset.playThreshold)
        this.playObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting
              if (entry.isIntersecting) {
                if (!this.userPaused && !this.reduceMotion) this.playActive()
              } else {
                this.pauseAll()
              }
            })
          },
          { threshold: isNaN(threshold) ? 0.35 : threshold }
        )
        this.playObserver.observe(this)
      }

      syncState() {
        const active = this.activeVideo
        const playing = active && !active.paused && !active.ended
        this.dataset.state = playing ? 'playing' : 'paused'
        if (this.button) {
          const label = playing
            ? this.button.dataset.labelPause
            : this.button.dataset.labelPlay
          // Label flips between play / pause (no aria-pressed: a toggle
          // button should change one or the other, not both).
          if (label) this.button.setAttribute('aria-label', label)
        }
      }
    }
  )
}
